import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ShoppingBag, ChevronDown } from 'lucide-react';
import { selectCartTotalQuantity, toggleCart } from '../store/slices/cartSlice';
import { selectCategories, selectSubCategories } from '../store/slices/dressesSlice';
import { selectSiteAlertActive } from '../store/slices/siteAlertSlice';
import Logo from '../assets/Logo.png';

const SCROLL_THRESHOLD = 40;

const MarketingHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const totalQuantity = useSelector(selectCartTotalQuantity);
  // Reuse the categories/subcategories App.js already fetches on mount for the
  // storefront filter sidebar, instead of firing a second, independent request
  // for the same data here.
  const categories = useSelector(selectCategories);
  const subCategories = useSelector(selectSubCategories);
  const alertActive = useSelector(selectSiteAlertActive);
  const [scrolled, setScrolled] = useState(false);

  // Only the actual home page has a hero banner for this header to
  // transparently overlay — everywhere else it should just look like a
  // normal solid header, regardless of scroll position.
  const isHomePage = location.pathname === '/';
  const scrolledEffective = isHomePage ? scrolled : true;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = scrolledEffective
    ? 'text-gray-700 hover:text-primary-600'
    : 'text-white/90 hover:text-white drop-shadow-sm';

  return (
    <header
      className={`hidden md:block fixed ${alertActive ? 'top-10' : 'top-0'} inset-x-0 z-40 transition-all duration-300 ${
        scrolledEffective ? 'bg-white shadow-sm' : 'bg-gradient-to-b from-black/45 via-black/10 to-transparent'
      }`}
    >
      <div className="w-full px-6 lg:px-10">
        <div className="grid grid-cols-[auto_1fr_auto] items-center h-20">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="justify-self-start flex items-center">
            <img src={Logo} alt="Store" className="h-9 w-auto" />
          </button>

          {/* Category Nav */}
          <nav className="justify-self-center flex items-center gap-8">
            {categories.map(cat => {
              const subs = subCategories.filter(sc => sc.categoryId === cat.id);
              return (
                <div key={cat.id} className="relative group">
                  <button
                    onClick={() => navigate(`/category/${cat.id}`)}
                    className={`flex items-center gap-1 text-sm font-medium tracking-wide whitespace-nowrap transition-colors ${linkClass}`}
                  >
                    {cat.name}
                    {subs.length > 0 && <ChevronDown className="w-3.5 h-3.5" />}
                  </button>

                  {subs.length > 0 && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 z-50">
                      <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[190px] max-h-80 overflow-y-auto">
                        {subs.map(sc => (
                          <button
                            key={sc.id}
                            onClick={() => navigate(`/subcategory/${sc.id}`)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                          >
                            {sc.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Cart */}
          <div className="justify-self-end flex items-center gap-3">
            <button
              onClick={() => dispatch(toggleCart())}
              className={`relative p-2 transition-colors ${linkClass}`}
              aria-label="Shopping cart"
            >
              <ShoppingBag className="w-6 h-6" />
              {totalQuantity > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary-600 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-0.5 leading-none">
                  {totalQuantity > 99 ? '99+' : totalQuantity}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MarketingHeader;
